<script setup lang="ts">
import Dropdown from "primevue/dropdown"
import httpAxiosApi from "capitalist-shared/src/core/axios.errors.handle";
import {reactive, computed} from "vue";
import {
  UserCategoryGroup
} from "@/backoffice/components/pages/settings/usercategory/user-category";
import {getCurrentInstance, ref} from "vue";
import VueI18n from "vue-i18n";
import Skeleton from "@/components/decorations/Skeleton.vue";
import {WebFormModel} from "capitalist-shared/src/core/model";
import {useCategories, useCurrentUserCategory} from "capitalist-shared/src/profile/categories";


const categoriesData = useCategories();
const whoamiData = useCurrentUserCategory();

const sortingOrder = [
  UserCategoryGroup.individual,
  UserCategoryGroup.business,
  UserCategoryGroup.business_service,
  UserCategoryGroup.business_advertising_network,
  UserCategoryGroup.business_affiliate_network,
  UserCategoryGroup.business_webmaster_service,
  UserCategoryGroup.business_marketing_agency,
];
const locale = computed(() => {
  return getCurrentInstance()?.proxy?.$i18n.locale || 'ru';
})

const ProfileUsertypeEnum = {
  Individual: 'INDIVIDUAL',
  Business: 'LEGAL',
}

const preferredUserType = ref(ProfileUsertypeEnum.Individual);

const filterUserType = computed(() => {
  if (!whoamiData.data?.value?.profile)
    return null;
  return whoamiData.data?.value?.profile?.isVerified
    ? whoamiData.data?.value?.profile?.type : preferredUserType.value;
})

const categories = computed(() => {
  return categoriesData.data?.value || [];
})

const categoryOptions = computed(() => {
  const $i18n = getCurrentInstance()?.proxy.$i18n as VueI18n;

  const result = categories.value.filter((val) => {
    if (val.id == whoamiData.data?.value?.profile?.categoryId) return true;
    if (!val.visible) return false;
    if (filterUserType.value) {
      return val.usertype == filterUserType.value;
    }
    return true;
  }).sort((a, b) => {
    const key = locale.value == 'ru' ? 'name' : 'name_en';
    return a[key].localeCompare(b[key]);
  }).sort((a, b) => {
    return - sortingOrder.indexOf(a.group) + sortingOrder.indexOf(b.group);
  }).sort((a, b) => {
    return a.other ? 1 : -1;
  }).map((val) => {
    return {...val,
      lname: $i18n.locale == 'ru' ? val.name : val.name_en,
      disabled: false};
  })

  sortingOrder.forEach((group) => {
    const gIndex = result.findIndex((val) => {
      return val.group == group;
    })

    if (gIndex >= 0)
    {
      result.splice(gIndex, 0, {
        id: group,
        name: $i18n.t('category_name.' + group) as string,
        name_en: $i18n.t('category_name.' + group) as string,
        lname: $i18n.t('category_name.' + group) as string,
        group: 'root',
        usertype: result[gIndex].usertype,
        visible: true,
        other: false,
        disabled: true,
      });
    }
  })


  const oIndex = result.findIndex((val) => {
    return val.other;
  })

  if (oIndex >= 0)
  {
    result.splice(oIndex, 0, {
      id: 'other',
      name: $i18n.t('Другое') as string,
      name_en: $i18n.t('Другое') as string,
      lname: $i18n.t('Другое') as string,
      group: 'root',
      usertype: result[oIndex].usertype,
      visible: true,
      other: false,
      disabled: true,
    });
  }

  // filter disabled
  return result.filter((val) => {
    return !val.disabled;
  })

  return result;
})

class UserCategoryUpdateModel extends WebFormModel {
  update(categoryId) {
    this.errors.clear();
    return httpAxiosApi.post('/webapi/profile/update/category', {
      categoryId: categoryId
    }).then((response) => {
      return response.data;
    }).catch((error) => {
      this.checkAxiosError(error);
      throw error;
    })
  }
}

const model = reactive(new UserCategoryUpdateModel());
const updating = ref(false);

const startEditMode = () => {
  preferredUserType.value = whoamiData.data?.value?.profile?.type;
  editMode.value = true;
}

const onUpdateCategory = (val) => {
  // console.log('onUpdateCategory', val, whoamiData.data?.profile?.categoryId);
  if (val === whoamiData.data?.value?.profile?.categoryId) {
    editMode.value = false;
    return;
  }

  updating.value = true;
  model.update(val).then((data) => {
    whoamiData.mutate(() => ({
      ...whoamiData.data?.value,
      ...data,
    }));
    updating.value = false;
    editMode.value = false;
  }).catch(() => {
    updating.value = false;
  });
}

const category = computed({
  get: () => {
    return computedCategory.value?.id;
  },
  set: (val) => {
    updating.value = true;
    model.update(val).then((data) => {
      whoamiData.mutate(() => ({
        ...whoamiData.data.value,
        ...data,
      }));
      updating.value = false;
      editMode.value = false;
    }).catch(() => {
      updating.value = false;
    });

    whoamiData.mutate(() => ({
      ...whoamiData.data?.value,
      profile: {
        ...whoamiData.data?.value.profile,
        categoryId: val
      }
    }));
  }
})

const editMode = ref(false)

const anyLoading = computed(() => {
  return categoriesData.loading?.value || whoamiData.loading?.value;
})

const anyError = computed(() => {
  return categoriesData.error?.value || whoamiData.error?.value;
})

const allData = computed(() => {
  return categoriesData.data?.value && whoamiData.data.value;
})

const computedCategory = computed(() => {
  if (!allData.value || !whoamiData.data?.value?.profile?.categoryId)
    return null;

  const result = categories.value.find((val) => {
    return val.id == whoamiData.data?.value?.profile?.categoryId;
  });
  const $i18n = getCurrentInstance()?.proxy.$i18n as VueI18n;

  return result ? {
    ...result,
    lname: $i18n.locale == 'ru' ? result.name : result.name_en,
  } : null
})

const preferredUserTypeOptions = computed(() => {
  const $i18n = getCurrentInstance()?.proxy.$i18n as VueI18n;

  return [
    {label: $i18n.t('Частное лицо'), value: ProfileUsertypeEnum.Individual},
    {label: $i18n.t('Бизнес'), value: ProfileUsertypeEnum.Business},
  ]
})

const getTypeOptionByValue = (value) => {
  return preferredUserTypeOptions.value.find((val) => {
    return val.value == value;
  })
}
</script>

<template>
<div class="ucinline--wrapper">
  <template v-if="anyLoading">
    <Skeleton height="32px" width="200px" />
  </template>
  <template v-if="anyError && !anyLoading">
    <div class="p-error">
      {{$t('Ошибка загрузки категории пользователя.')}}
    </div>
  </template>
  <template v-if="allData && !anyLoading">
    <template v-if="!editMode">
      <template v-if="computedCategory">
      <a href="#" @click.prevent="() => startEditMode()" class="link-hovered available_for_edit editable-contact editable editable-click">
      {{computedCategory?.lname}}
      </a>
      </template>
      <template v-else>
        <a href="#" @click.prevent="() => startEditMode()" class="link-hovered available_for_edit editable-contact editable editable-click">
          {{$t('Выберите категорию...')}}
        </a>
      </template>
    </template>
    <template v-if="editMode">
      <template v-if="categoriesData.data?.value && !categoriesData.loading?.value">
        <div class="dropdowns-list">
          <Dropdown
            :disabled="updating"
            class="ucinline--dropdown"
            v-model="category"
            :filter="true"
            :showClear="true"
            :emptyFilterMessage="$t('Нет данных для отображения.')"
            :optionLabel="(data) => data.lname"
            :optionValue="(data) => data.id" dataKey="id"
            :optionDisabled="(data) => data.disabled"
            :placeholder="$t('Выберите из списка...')"
            :options="categoryOptions">
            <template #option="slotProps">
              <template v-if="slotProps.option.disabled">
                <div class="p-dropdown-group-option">{{slotProps.option.lname}}</div>
              </template>
              <template v-else>
                <div class="p-dropdown-activity-option">{{slotProps.option.lname}}</div>
              </template>
            </template>
          </Dropdown>

          <Dropdown
            :disabled="whoamiData.data?.value?.profile?.isVerified"
            key="preferredUserType"
            class="ucinline--dropdown"
            :optionLabel="(data) => data.label"
            :optionValue="(data) => data.value"
            dataKey="value"
            :emptyFilterMessage="$t('Нет данных для отображения.')"
            v-model="preferredUserType"
            :options="preferredUserTypeOptions">
            <template #value="slotProps">
              <div v-if="slotProps.value">{{getTypeOptionByValue(slotProps.value)?.label || slotProps.value}}</div>
              <div v-else>{{slotProps.placeholder}}</div>
            </template>
            <template #option="slotProps">
              <div>{{slotProps.option.label}}</div>
            </template>
          </Dropdown>
        </div>
        <div v-if="model.errors.has()">
          <div class="p-invalid">
            {{model.errors.getFirstError()}}
          </div>
        </div>
      </template>
    </template>
  </template>

</div>
</template>

<style scoped lang="less">
.ucinline--wrapper {
  display: contents;;
}
.ucinline--dropdown {
  ::v-deep {
    &.p-dropdown {

      .p-inputwrapper {

      }
      .p-dropdown-label {
        padding: 1rem 1.5rem;
        font-size: 1.4rem;

      }

      .p-dropdown-panel {
        box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
        //max-width: 400px;
      }
      .p-dropdown-group-option
      {
        color: #000;
        font-size: 0.8em;
        margin-top: 0.7em;
        border-bottom: 1px solid #f0f0f0;
      }
      .p-dropdown-item {
        font-size: 1.4rem;
        padding: 1rem 1.5rem;
        &.p-highlight {
          background: #93bb36;
        }
      }
      .p-dropdown-filter.p-inputtext.p-component {
        padding: 0 1.5rem;
      }
      .p-dropdown-filter {
        //padding: 0.5rem 1.5rem;
      }
      .p-dropdown-panel {
        .p-component {
          font-size: 1.4rem;

        }
      }
    }
    .p-hidden-accessible {
      display: none;
    }
  }
}
.dropdowns-list {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  .ucinline--dropdown {
    width: 100%;
  }
  gap: 2rem;
}
.p-invalid {
  cursor: pointer;
  color: red;
  font-size: 1.25rem;
  padding-top: 0.5rem;
}
</style>

<i18n>
{
  "en": {
    "Сервисы": "Services",
    "Другое": "Other",
    "category_name": {
      "business": "Business",
      "individual": "Individual",
      "business_service": "Services",
      "business_advertising_network": "Advertising networks",
      "business_affiliate_network": "Affiliate programs",
      "business_webmaster_service": "Webmaster services",
      "business_marketing_agency": "Marketing agencies"
    },
    "Частное лицо": "Individual",
    "Бизнес": "Business",
    "Нет данных для отображения.": "No data to display.",
    "Выберите категорию...": "Choose a category...",
    "Выберите из списка...": "Choose from the list..."
  },
  "ru": {
    "category_name": {
      "business": "Бизнес",
      "individual": "Частное лицо",
      "business_service": "Сервисы",
      "business_advertising_network": "Рекламные сети",
      "business_affiliate_network": "Партнерские программы",
      "business_webmaster_service": "Сервисы для вебмастеров",
      "business_marketing_agency": "Маркетинговые агентства"
    }
  }
}
</i18n>
